<script setup lang="ts">
import { computed, markRaw } from 'vue';
import SearchResultFieldSections, { type InspectorFieldOverride } from '@/components/DataTable/SearchResultFieldSections.vue';
import MultipleValues from '@/components/record/MultipleValues.vue';
import RecordChips from '@/components/record/RecordChips.vue';
import { combineRows } from '@/components/record/combinedRecord';
import { fieldKeyOf, fieldKindFor } from '@/components/record/fields/fieldKind';
import { buildEntityColumns } from '@/composables/entityColumns';
import { inspectorExcludeFieldsFor } from '@/composables/searchInspectorRegistry';
import { applyLayout, chipLabelsOf, type RecordLayoutDefinition } from '@/composables/recordLayout/recordLayoutDefinition';

/**
 * The body of the combined record shown when several records are selected:
 * the person's chips and sections, with each field showing the value the
 * records share, a dash when all of them are empty, or "Multiple values".
 * Spec: docs/specs/sau-600-combined-record.md.
 *
 * Every record type draws these plain sections, never its own tabbed
 * inspector, so nothing here polls or fetches by one record's id. It provides
 * no record page context and no pane navigator, so a shared related link
 * opens the side peek.
 */
const props = defineProps<{
    entityId: string;
    /** The selected rows, two or more, as the list holds them. */
    rows: Record<string, unknown>[];
    layout: RecordLayoutDefinition;
}>();

const columns = computed(() => buildEntityColumns(props.entityId));
const combined = computed(() => combineRows(props.rows));
const differingKeys = computed(() => [...combined.value.differingKeys]);

// The chips go through no exclusions, exactly as on one record, so a player's Online and Last checkin still show.
const headerColumns = computed(() => applyLayout(props.layout, columns.value, { entityId: props.entityId }).headerColumns);
const hideLabels = computed(() => chipLabelsOf(props.layout) === 'hide');

/**
 * The fields one record's Details leave out, and every related-record list,
 * such as a zone's Players. `name` stays: one record drops it only because
 * its title shows it, and the combined record has no title.
 */
const excludeFields = computed(() => [
    ...inspectorExcludeFieldsFor(props.entityId),
    'display_string',
    ...columns.value.filter((column) => fieldKindFor(props.entityId, column) === 'relatedList').map((column) => column.id),
]);
const groups = computed(() => applyLayout(props.layout, columns.value, { excludeFields: excludeFields.value, entityId: props.entityId }).groups);

/** A field the records differ on draws "Multiple values" in place of its value. */
const fieldOverrides = computed(() => {
    const overrides: Record<string, InspectorFieldOverride> = {};
    for (const group of groups.value) {
        for (const column of group.columns) {
            if (combined.value.differingKeys.has(fieldKeyOf(column))) {
                overrides[column.key] = { component: markRaw(MultipleValues) };
            }
        }
    }
    return overrides;
});
</script>

<template>
  <div class="flex flex-col gap-4" data-testid="combined-record-body">
    <RecordChips :columns="headerColumns" :row="combined.row" :hide-labels="hideLabels" :multiple-keys="differingKeys" />
    <div class="@container">
      <SearchResultFieldSections :row="combined.row" :groups="groups" :entity-id="entityId" :field-overrides="fieldOverrides" />
    </div>
  </div>
</template>
